// SEO helpers shared by public routes. Pure functions, no side effects.
import { SITE_URL } from "./site-url";

const BASE = SITE_URL.replace(/\/+$/, "");
const DEFAULT_OG_IMAGE = `${BASE}/og-image.png`;

export function canonical(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${BASE}${p}`;
}

export interface PageMetaInput {
  path: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: "website" | "article";
  image?: string; // absolute URL
}

export function pageMeta(input: PageMetaInput): {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
} {
  const url = canonical(input.path);
  const ogTitle = input.ogTitle ?? input.title;
  const ogDescription = input.ogDescription ?? input.description;
  const image = input.image ?? DEFAULT_OG_IMAGE;
  return {
    meta: [
      { title: input.title },
      { name: "description", content: input.description },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: input.ogType ?? "website" },
      { property: "og:site_name", content: "Truck Company Center" },
      { property: "og:locale", content: "es_US" },
      { property: "og:image", content: image },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: ogTitle },
      { name: "twitter:description", content: ogDescription },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

// Returns a JSON string suitable for either:
//   - head.scripts[].children
//   - dangerouslySetInnerHTML={{ __html: ... }} inside a <script> tag
// Escapes "</script" so the JSON cannot break out of a script tag.
export function jsonLdScriptTag(obj: unknown): string {
  return JSON.stringify(obj).replace(/<\/script/gi, "<\\/script");
}

export const SITE_BASE_URL = BASE;
export const OG_IMAGE_URL = DEFAULT_OG_IMAGE;

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Truck Company Center",
    url: BASE,
    logo: `${BASE}/logo.png`,
    image: DEFAULT_OG_IMAGE,
    description:
      "Asistencia administrativa para nuevos carriers, owner-operators y pequeñas trucking companies en Estados Unidos.",
    areaServed: "US",
  } as const;
}

export function professionalServiceJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Truck Company Center",
    alternateName: "TCC",
    url: BASE,
    logo: `${BASE}/android-chrome-512x512.png`,
    image: DEFAULT_OG_IMAGE,
    description:
      "Asistencia administrativa para car carriers y owner-operators: LLC en Florida, EIN, USDOT, MC, BOL, W-9, Central Dispatch y Super Dispatch.",
    areaServed: "United States",
    priceRange: "$$",
    serviceType: [
      "LLC setup",
      "EIN assistance",
      "USDOT assistance",
      "MC authority assistance",
      "Central Dispatch account setup",
      "Super Dispatch account setup",
      "BOL preparation",
      "W-9 preparation",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      availableLanguage: ["Spanish", "English"],
    },
  } as const;
}
